import { zodResolver } from "@hookform/resolvers/zod";
import type { TeeDefectDetailsData } from "@stryker/core/types/sections";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep } from "@/constants/inputs";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";
import { convertLength, feetToInches } from "@/utils/mappings";

const lengthTypes = [
  { value: "symmetrical", title: "Symmetrical" },
  { value: "asymmetrical", title: "Asymmetrical" },
] as const;

type LengthType = (typeof lengthTypes)[number]["value"];

const teeDefectSchema = z.union([
  z
    .object({
      defect_length_1: z.number(),
      length_type: z.enum(["symmetrical", "asymmetrical"]),
      desired_overall_length_header: z.number().nullish(),
      distance_from_center_of_riser_left: z.number().nullish(),
      distance_from_center_of_riser_right: z.number().nullish(),
      unit_for_defect_length_1: z.string(),
      unit_for_desired_overall_length_header: z.string().optional(),
      unit_for_distance_from_center_of_riser_left: z.string().optional(),
      unit_for_distance_from_center_of_riser_right: z.string().optional(),
    })
    .refine(
      (data) => {
        if (data.length_type === "asymmetrical") {
          return true;
        }
        const isSameUnit = data.unit_for_defect_length_1 === data.unit_for_desired_overall_length_header;
        if (isSameUnit) {
          return Number(data.desired_overall_length_header) >= Number(data.defect_length_1);
        } else {
          if (data.unit_for_defect_length_1 === "Inches") {
            // desired is in feet, convert desired to inches
            return feetToInches(data.desired_overall_length_header ?? 0) >= Number(data.defect_length_1);
          } else {
            // defect is in feet, convert defect to inches equivalent by converting defect to inches and comparing numbers appropriately
            // Here we want desired (in inches) >= defect (in feet converted to inches)
            return Number(data.desired_overall_length_header) >= feetToInches(data.defect_length_1);
          }
        }
      },
      {
        message: "How much do you wish to wrap must be greater or equal to total defect length",
        path: ["desired_overall_length_header"],
      }
    )
    .refine(
      (data) => {
        if (data.length_type !== "asymmetrical") {
          return true;
        }
        const defectUnit = data.unit_for_defect_length_1;
        const leftInDefectUnit = convertLength(
          data.distance_from_center_of_riser_left ?? 0,
          data.unit_for_distance_from_center_of_riser_left ?? defectUnit,
          defectUnit
        );
        const rightInDefectUnit = convertLength(
          data.distance_from_center_of_riser_right ?? 0,
          data.unit_for_distance_from_center_of_riser_right ?? defectUnit,
          defectUnit
        );
        return leftInDefectUnit + rightInDefectUnit >= Number(data.defect_length_1);
      },
      {
        message: "Distance from center of riser (left + right) must be greater or equal to total defect length",
        path: ["distance_from_center_of_riser_right"],
      }
    ),
  z
    .object({
      defect_length_2: z.number(),
      passed_header: z.number(),
      unit_for_defect_length_2: z.string(),
      unit_for_passed_header: z.string(),
    })
    .refine(
      (data) => {
        const isSameUnit = data.unit_for_defect_length_2 === data.unit_for_passed_header;
        if (isSameUnit) {
          return Number(data.passed_header) >= Number(data.defect_length_2);
        } else {
          if (data.unit_for_defect_length_2 === "Inches") {
            return feetToInches(data.passed_header) >= Number(data.defect_length_2);
          } else {
            return Number(data.passed_header) >= feetToInches(data.defect_length_2);
          }
        }
      },
      {
        message: "How much do you wish to wrap must be greater or equal to total defect length",
        path: ["passed_header"],
      }
    ),
]);

const teeDefectDetailsSchema = z.object({
  defects: z.array(teeDefectSchema),
});

type TeeDefectDetailsSchema = z.infer<typeof teeDefectDetailsSchema>;

interface TeeDefectDetailsProps {
  onSave: (data: TeeDefectDetailsData) => void;
}

type TeeDefectType1 = {
  defect_length_1: number;
  length_type?: LengthType;
  desired_overall_length_header?: number | null;
  distance_from_center_of_riser_left?: number | null;
  distance_from_center_of_riser_right?: number | null;
  unit_for_defect_length_1: string;
  unit_for_desired_overall_length_header?: string;
  unit_for_distance_from_center_of_riser_left?: string;
  unit_for_distance_from_center_of_riser_right?: string;
};

type TeeDefectType2 = {
  defect_length_2: number;
  passed_header: number;
  unit_for_defect_length_2: string;
  unit_for_passed_header: string;
};

type TeeDefectFromApi = TeeDefectDetailsData["defects"][number];

// The API can return each defect entry either "pure" (only Header or only Riser
// keys present) or merged (both Header and Riser keys present on the same
// object, e.g. for duplicated jobs). Use actual values rather than key
// presence so a merged object is classified by what it holds, not what keys
// happen to exist on it.
const hasHeaderValue = (defect: TeeDefectFromApi) =>
  defect.defect_length_1 !== undefined && defect.defect_length_1 !== null;
const hasRiserValue = (defect: TeeDefectFromApi) =>
  (defect.defect_length_2 !== undefined && defect.defect_length_2 !== null) ||
  (defect.passed_header !== undefined && defect.passed_header !== null);

// The API can send numeric fields as numeric strings (e.g. decimal columns
// serialized as strings). The schema expects `number | null`, so coerce here
// rather than trusting the declared type. Unit fields default to "Inches"
// when the API doesn't return one, matching the empty-state defaults below.
const toNumberOrNull = (value: unknown): number | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null || value === "") return null;
  const parsed = typeof value === "number" ? value : Number.parseFloat(String(value));
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeHeaderDefect = (defect: TeeDefectType1): TeeDefectType1 => ({
  ...defect,
  defect_length_1: toNumberOrNull(defect.defect_length_1) ?? 0,
  desired_overall_length_header: toNumberOrNull(defect.desired_overall_length_header),
  distance_from_center_of_riser_left: toNumberOrNull(defect.distance_from_center_of_riser_left),
  distance_from_center_of_riser_right: toNumberOrNull(defect.distance_from_center_of_riser_right),
  unit_for_defect_length_1: defect.unit_for_defect_length_1 || "Inches",
  unit_for_desired_overall_length_header: defect.unit_for_desired_overall_length_header || "Inches",
  unit_for_distance_from_center_of_riser_left: defect.unit_for_distance_from_center_of_riser_left || "Inches",
  unit_for_distance_from_center_of_riser_right: defect.unit_for_distance_from_center_of_riser_right || "Inches",
});

const normalizeRiserDefect = (defect: TeeDefectType2): TeeDefectType2 => ({
  ...defect,
  defect_length_2: toNumberOrNull(defect.defect_length_2) ?? 0,
  passed_header: toNumberOrNull(defect.passed_header) ?? 0,
  unit_for_defect_length_2: defect.unit_for_defect_length_2 || "Inches",
  unit_for_passed_header: defect.unit_for_passed_header || "Inches",
});

const withAsymmetricalFlag = (defect: TeeDefectType1): TeeDefectType1 & { length_type: LengthType } => {
  const hasAsymmetricalData =
    (defect.distance_from_center_of_riser_left !== undefined && defect.distance_from_center_of_riser_left !== null) ||
    (defect.distance_from_center_of_riser_right !== undefined && defect.distance_from_center_of_riser_right !== null);
  return { ...defect, length_type: hasAsymmetricalData ? "asymmetrical" : "symmetrical" };
};

// Normalizes the API's `defects` array into exactly one Header entry followed
// by one Riser entry. Handles both the expected shape (two separate entries)
// and the merged shape returned for some jobs (e.g. duplicated tee jobs),
// where a single entry carries both Header and Riser fields. Also coerces
// numeric fields and defaults missing units to "Inches".
const normalizeTeeDefects = (defects: TeeDefectFromApi[]): (TeeDefectType1 | TeeDefectType2)[] => {
  const headerSource = defects.find(hasHeaderValue);
  const riserSource = defects.find(hasRiserValue);

  const header: TeeDefectType1 = normalizeHeaderDefect(withAsymmetricalFlag((headerSource ?? {}) as TeeDefectType1));
  const riser: TeeDefectType2 = normalizeRiserDefect((riserSource ?? {}) as TeeDefectType2);

  return [header, riser];
};

export function TeeDefectDetails({ onSave }: TeeDefectDetailsProps) {
  const { sections } = useCUMechanicalJobStore();
  const savedDefectDetails = sections.defectDetails;
  const componentDetailsData = sections.componentDetails.data;
  const components = componentDetailsData.components_data;
  const { measureUnitOptions } = useConfigOptions();

  const form = useForm<TeeDefectDetailsSchema>({
    resolver: zodResolver(teeDefectDetailsSchema),
    defaultValues: {
      defects:
        (savedDefectDetails.data as TeeDefectDetailsData).defects &&
        (savedDefectDetails.data as TeeDefectDetailsData).defects.length > 0
          ? normalizeTeeDefects((savedDefectDetails.data as TeeDefectDetailsData).defects)
          : [
              {
                defect_length_1: 0,
                length_type: "symmetrical",
                desired_overall_length_header: 0,
                unit_for_defect_length_1: "Inches",
                unit_for_desired_overall_length_header: "Inches",
                distance_from_center_of_riser_left: 0,
                distance_from_center_of_riser_right: 0,
                unit_for_distance_from_center_of_riser_left: "Inches",
                unit_for_distance_from_center_of_riser_right: "Inches",
              },
              {
                defect_length_2: 0,
                passed_header: 0,
                unit_for_defect_length_2: "Inches",
                unit_for_passed_header: "Inches",
              },
            ],
    },
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: "defects",
  });

  const watchedDefects = form.watch("defects");

  const handleSave = (payload: TeeDefectDetailsSchema) => {
    const data: TeeDefectDetailsData = {
      ...payload,
      repair_length_options: (savedDefectDetails.data as TeeDefectDetailsData).repair_length_options,
      repair_length_repair_kit_sizes: (savedDefectDetails.data as TeeDefectDetailsData).repair_length_repair_kit_sizes,
    };
    onSave(data);
  };

  // `defects` is normalized (see normalizeTeeDefects) to always be
  // [Header, Riser], so the section is determined by position, not by which
  // keys/values happen to be present on the object.
  const isType1Defect = (_defect: TeeDefectType1 | TeeDefectType2, index: number): _defect is TeeDefectType1 => {
    return index === 0;
  };

  // Clear the fields belonging to the length type the user is switching away from,
  // so stale values aren't left in the payload.
  const handleLengthTypeChange = (index: number, value: LengthType) => {
    form.setValue(`defects.${index}.length_type`, value);
    if (value === "asymmetrical") {
      form.setValue(`defects.${index}.desired_overall_length_header`, null);
    } else {
      form.setValue(`defects.${index}.distance_from_center_of_riser_left`, null);
      form.setValue(`defects.${index}.distance_from_center_of_riser_right`, null);
    }
  };

  // Helper function to build component title
  const getComponentTitle = (index: number) => {
    const component = components[index];
    if (!component) return "";

    if (component.component_nps !== "custom") {
      return `${component.component_nps} NPS ${component.component_schedule}`;
    } else {
      return `Custom  ${component.component_diameter} ${component.unit_for_component_diameter} OD X ${component.component_thickness} ${component.unit_for_component_thickness} THK`;
    }
  };

  return (
    <div className="space-y-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSave)} className="flex flex-col justify-between gap-4">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className={index !== fields.length - 1 ? "border-black border-b border-solid pb-4" : ""}
            >
              <Typography variant="text-md" weight="semibold" className="mb-2">
                {isType1Defect(field, index) ? "Header" : "Riser"}: {getComponentTitle(index)}
              </Typography>

              {isType1Defect(field, index) ? (
                <>
                  <div className="mb-4 flex gap-2">
                    <FormField
                      control={form.control}
                      name={`defects.${index}.defect_length_1`}
                      render={({ field }) => (
                        <FormItem className="flex flex-1 flex-col">
                          <FormLabel className="text-left">Defect Length 1</FormLabel>
                          <FormControl>
                            <TextInput
                              type="number"
                              step={inputStep}
                              placeholder="Enter defect length 1"
                              {...field}
                              onChange={(e) => field.onChange(Number.parseFloat((e.target as HTMLInputElement).value))}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`defects.${index}.unit_for_defect_length_1`}
                      render={({ field }) => (
                        <FormItem className="flex flex-1 flex-col">
                          <FormLabel>Unit</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {measureUnitOptions.map((unit, unitIndex) => (
                                <SelectItem
                                  key={`tee-unit-${unit.value}-${index}-${unitIndex ?? 0}`}
                                  value={unit.value}
                                >
                                  {unit.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="mb-4 flex gap-2">
                    <FormField
                      control={form.control}
                      name={`defects.${index}.length_type`}
                      render={({ field }) => (
                        <FormItem className="flex flex-1 flex-col">
                          <FormLabel className="text-left">Length Type</FormLabel>
                          <Select
                            onValueChange={(value: LengthType) => handleLengthTypeChange(index, value)}
                            defaultValue={field.value ?? "symmetrical"}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {lengthTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {(watchedDefects?.[index] as TeeDefectType1 | undefined)?.length_type === "asymmetrical" ? (
                    <>
                      <div className="mb-4 flex gap-2">
                        <FormField
                          control={form.control}
                          name={`defects.${index}.distance_from_center_of_riser_left`}
                          render={({ field }) => (
                            <FormItem className="flex flex-1 flex-col">
                              <FormLabel className="text-left">Distance from Center of Riser — Left</FormLabel>
                              <FormControl>
                                <TextInput
                                  type="number"
                                  step={inputStep}
                                  placeholder="Enter distance from center of riser - left"
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) =>
                                    field.onChange(Number.parseFloat((e.target as HTMLInputElement).value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`defects.${index}.unit_for_distance_from_center_of_riser_left`}
                          render={({ field }) => (
                            <FormItem className="flex flex-1 flex-col">
                              <FormLabel>Unit</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {measureUnitOptions.map((unit, unitIndex) => (
                                    <SelectItem
                                      key={`tee-unit-${unit.value}-${index}-${unitIndex ?? 0}`}
                                      value={unit.value}
                                    >
                                      {unit.title}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="flex gap-2">
                        <FormField
                          control={form.control}
                          name={`defects.${index}.distance_from_center_of_riser_right`}
                          render={({ field }) => (
                            <FormItem className="flex flex-1 flex-col">
                              <FormLabel className="text-left">Distance from Center of Riser — Right</FormLabel>
                              <FormControl>
                                <TextInput
                                  type="number"
                                  step={inputStep}
                                  placeholder="Enter distance from center of riser - right"
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) =>
                                    field.onChange(Number.parseFloat((e.target as HTMLInputElement).value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`defects.${index}.unit_for_distance_from_center_of_riser_right`}
                          render={({ field }) => (
                            <FormItem className="flex flex-1 flex-col">
                              <FormLabel>Unit</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {measureUnitOptions.map((unit, unitIndex) => (
                                    <SelectItem
                                      key={`tee-unit-${unit.value}-${index}-${unitIndex ?? 0}`}
                                      value={unit.value}
                                    >
                                      {unit.title}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="flex gap-2">
                      <FormField
                        control={form.control}
                        name={`defects.${index}.desired_overall_length_header`}
                        render={({ field }) => (
                          <FormItem className="flex flex-1 flex-col">
                            <FormLabel className="text-left">Desired Overall Length Header</FormLabel>
                            <FormControl>
                              <TextInput
                                type="number"
                                step={inputStep}
                                placeholder="Enter desired overall length header"
                                {...field}
                                value={field.value ?? ""}
                                onChange={(e) =>
                                  field.onChange(Number.parseFloat((e.target as HTMLInputElement).value))
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`defects.${index}.unit_for_desired_overall_length_header`}
                        render={({ field }) => (
                          <FormItem className="flex flex-1 flex-col">
                            <FormLabel>Unit</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {measureUnitOptions.map((unit, unitIndex) => (
                                  <SelectItem
                                    key={`tee-unit-${unit.value}-${index}-${unitIndex ?? 0}`}
                                    value={unit.value}
                                  >
                                    {unit.title}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-4 flex gap-2">
                    <FormField
                      control={form.control}
                      name={`defects.${index}.defect_length_2`}
                      render={({ field }) => (
                        <FormItem className="flex flex-1 flex-col">
                          <FormLabel className="text-left">Defect Length 2</FormLabel>
                          <FormControl>
                            <TextInput
                              type="number"
                              step={inputStep}
                              placeholder="Enter defect length 2"
                              {...field}
                              onChange={(e) => field.onChange(Number.parseFloat((e.target as HTMLInputElement).value))}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`defects.${index}.unit_for_defect_length_2`}
                      render={({ field }) => (
                        <FormItem className="flex flex-1 flex-col">
                          <FormLabel>Unit</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {measureUnitOptions.map((unit, unitIndex) => (
                                <SelectItem
                                  key={`tee-unit-${unit.value}-${index}-${unitIndex ?? 0}`}
                                  value={unit.value}
                                >
                                  {unit.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex gap-2">
                    <FormField
                      control={form.control}
                      name={`defects.${index}.passed_header`}
                      render={({ field }) => (
                        <FormItem className="flex flex-1 flex-col">
                          <FormLabel className="text-left">Passed Header</FormLabel>
                          <FormControl>
                            <TextInput
                              type="number"
                              step={inputStep}
                              placeholder="Enter passed header"
                              {...field}
                              onChange={(e) => field.onChange(Number.parseFloat((e.target as HTMLInputElement).value))}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`defects.${index}.unit_for_passed_header`}
                      render={({ field }) => (
                        <FormItem className="flex flex-1 flex-col">
                          <FormLabel>Unit</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {measureUnitOptions.map((unit, unitIndex) => (
                                <SelectItem
                                  key={`tee-unit-${unit.value}-${index}-${unitIndex ?? 0}`}
                                  value={unit.value}
                                >
                                  {unit.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </>
              )}
            </div>
          ))}

          <Button type="submit" className="ml-auto self-end">
            Save
          </Button>
        </form>
      </Form>
    </div>
  );
}
